/**
 * Share button and dialog for a tracker, on the same `ManageMembersModal`
 * shell Projects' "Share" uses. General access decides who in the
 * organization can open it: everyone (public) or the people listed (private),
 * besides organization admins, who always can. The people come from the
 * organization's tracker people, and the owner is always kept.
 */
import { useMemo, useState } from 'react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { useQueries, useQuery } from '@tanstack/react-query';
import { GlobeIcon, LockIcon, Share2 } from 'lucide-react';
import {
  trackerMembersQueryOptions,
  trackerPeopleQueryOptions,
  useUpdateTrackerMembersMutation,
  useUpdateTrackerMutation,
  type Tracker,
  type TrackerAccessLevel,
  type TrackerPerson,
} from '@/entities/tracker';
import type { ProjectMember, WorkspaceUser } from '@/entities/project-member';
import { userByIdQueryOptions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ManageMembersModal } from '@/features/manage-project-members';
import { boardTypeOf } from '@/widgets/trackers-board';
import { track } from '@/shared/analytics';
import { notificationService } from '@/lib/services/notification.service';
import { useDebounce, useModalState } from '@/shared/hooks';
import { useTranslation } from 'react-i18next';
import { useTrackerAccess } from './hooks/useTrackerAccess';

const ACCESS_EDIT: TrackerAccessLevel = 'edit';
const ACCESS_VIEW: TrackerAccessLevel = 'view';
const SEARCH_DEBOUNCE_MS = 250;

type Visibility = Tracker['visibility'];

export function TrackerShareButton({
  tracker,
  size = 'default',
}: {
  tracker: Tracker;
  /** `lg` is the 40px rung the redesigned board header sits its actions on. */
  size?: 'default' | 'lg';
}) {
  const { t } = useTranslation('trackers');

  const modal = useModalState();
  return (
    <>
      <Button variant="outline" size={size} onClick={modal.open}>
        <Share2 className="size-4" />
        {t('trackers.trackerShareButton.share')}
      </Button>
      {modal.isMounted ? (
        <TrackerShareModal
          key={modal.instanceKey}
          open={modal.isOpen}
          onClose={modal.close}
          tracker={tracker}
        />
      ) : null}
    </>
  );
}

export function TrackerShareModal({
  open,
  onClose,
  tracker,
}: {
  open: boolean;
  onClose: () => void;
  tracker: Tracker;
}) {
  const { t } = useTranslation('trackers');

  const workspaceId = useCurrentWorkspaceId() ?? '';
  const { canManage } = useTrackerAccess(tracker);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput.trim(), SEARCH_DEBOUNCE_MS);
  const [visibility, setVisibility] = useState<Visibility>(tracker.visibility);

  const pickerQuery = useQuery(trackerPeopleQueryOptions(search));
  const peopleQuery = useQuery(trackerPeopleQueryOptions(''));
  const membersQuery = useQuery(trackerMembersQueryOptions(workspaceId, tracker.id));
  const updateMembers = useUpdateTrackerMembersMutation({ workspaceId, trackerId: tracker.id });
  const updateTracker = useUpdateTrackerMutation({ workspaceId });

  // A default board belongs to the organization, so its recorded creator is not pinned as owner.
  const ownerId =
    membersQuery.data?.ownerId || (tracker.defaultKey ? undefined : tracker.createdBy);
  const memberUserIds = membersQuery.data?.userIds;
  const memberIds = useMemo(
    () => [...new Set([...(ownerId ? [ownerId] : []), ...(memberUserIds ?? [])])],
    [ownerId, memberUserIds],
  );

  // Members beyond the first page of people are named from the user directory.
  const known = useMemo(
    () =>
      new Map(
        [...(peopleQuery.data ?? []), ...(pickerQuery.data ?? [])].map((person) => [
          person.id,
          person,
        ]),
      ),
    [peopleQuery.data, pickerQuery.data],
  );
  const lookups = useQueries({
    queries: memberIds
      .filter((id) => !known.has(id))
      .map((id) => ({ ...userByIdQueryOptions(id), enabled: !peopleQuery.isPending })),
  });
  const looked = new Map(lookups.flatMap(({ data }) => (data ? [[data.id, data] as const] : [])));

  const members: ProjectMember[] = memberIds.map((id) => {
    const person: Pick<TrackerPerson, 'name' | 'email' | 'avatarUrl'> | undefined =
      known.get(id) ?? looked.get(id);
    return {
      id,
      name: person?.name ?? t('trackers.trackerShareButton.unknownUser'),
      email: person?.email ?? '',
      avatarUrl: person?.avatarUrl ?? null,
      permissions: [],
      createdAt: '',
      updatedAt: '',
    };
  });

  const pickerUsers = useMemo<WorkspaceUser[]>(
    () =>
      (pickerQuery.data ?? []).map((person) => ({
        id: person.id,
        name: person.name,
        email: person.email ?? undefined,
        avatarUrl: person.avatarUrl ?? null,
      })),
    [pickerQuery.data],
  );

  // Edit first: someone added here starts on the first level listed.
  const accessLevels = useMemo(
    () => [
      { id: ACCESS_EDIT, roleName: t('trackers.trackerShareButton.canEdit') },
      { id: ACCESS_VIEW, roleName: t('trackers.trackerShareButton.canView') },
    ],
    [t],
  );
  const assignments = useMemo(
    () =>
      (membersQuery.data?.members ?? []).map((m) => ({
        identityId: m.userId,
        roleId: m.accessLevel,
      })),
    [membersQuery.data],
  );

  const isPublic = visibility === 'public';
  const visibilityChanged = visibility !== tracker.visibility;

  const handleSave = async (
    stagedIds: string[],
    levels?: Array<{ userId: string; roleId: string }>,
  ) => {
    const levelOf = new Map(levels?.map((l) => [l.userId, l.roleId]));
    const explicit = stagedIds
      .filter((id) => id !== ownerId)
      .map((userId) => ({
        userId,
        accessLevel: levelOf.get(userId) === ACCESS_VIEW ? ACCESS_VIEW : ACCESS_EDIT,
      }));
    try {
      // Members are saved before a board turns private, so a failed member write never
      // leaves it private with the old list. A public board keeps its saved members
      // untouched, for when it is made private again.
      if (!isPublic) await updateMembers.mutateAsync(explicit);
      if (visibilityChanged) {
        await updateTracker.mutateAsync({ trackerId: tracker.id, body: { visibility } });
      }
    } catch {
      // The request's own error toast has already said why.
      return;
    }
    track('tracker_shared', {
      tracker_id: tracker.id,
      board_type: boardTypeOf(tracker.itemSource),
      member_count: isPublic ? 0 : explicit.length,
      is_restricted: !isPublic,
    });
    notificationService.showSuccess(t('trackers.trackerShareButton.trackerSharingUpdated'), {
      anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
    });
    // `onClose` alone: the host unmounts on its own timer.
    onClose();
  };

  return (
    <ManageMembersModal
      open={open}
      onClose={onClose}
      title={t('trackers.trackerShareButton.shareTracker')}
      peopleHeading={t('trackers.trackerShareButton.peopleWithAccess')}
      generalAccess={
        <GeneralAccess value={visibility} onChange={setVisibility} disabled={!canManage} />
      }
      showPeople={!isPublic}
      pickerUnderHeading
      addLabel={t('trackers.trackerShareButton.addPeopleFromOrganization')}
      hostHasChanges={visibilityChanged}
      canManage={canManage}
      ownerId={ownerId}
      members={members}
      workspaceUsers={pickerUsers}
      isLoadingUsers={pickerQuery.isPending}
      searchInput={searchInput}
      onSearchChange={setSearchInput}
      onSave={(ids, levels) => void handleSave(ids, levels)}
      isSaving={updateMembers.isPending || updateTracker.isPending}
      saveLabel={t('trackers.trackerShareButton.save')}
      loadError={
        membersQuery.isError
          ? {
              title: t('trackers.trackerShareButton.couldNotLoadAccess'),
              description: t('trackers.trackerShareButton.couldNotLoadAccessDescription'),
              retryLabel: t('trackers.trackerShareButton.retry'),
              onRetry: () => void membersQuery.refetch(),
            }
          : undefined
      }
      roles={{
        enabled: true,
        canEdit: true,
        availableRoles: accessLevels,
        assignments,
        isLoading: membersQuery.isLoading,
        selectorLabel: (name, level) =>
          t('trackers.trackerShareButton.accessLevelFor', { name, level }),
      }}
    />
  );
}

function GeneralAccess({
  value,
  onChange,
  disabled,
}: {
  value: Visibility;
  onChange: (next: Visibility) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('trackers');
  const isPublic = value === 'public';
  const Icon = isPublic ? GlobeIcon : LockIcon;

  return (
    <section className="flex flex-col gap-md" data-testid="tracker-general-access">
      <h3 className="text-base font-medium text-text-primary">
        {t('trackers.trackerShareButton.generalAccess')}
      </h3>
      <div className="flex items-center gap-md">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Icon className="size-5" aria-hidden />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium text-foreground">
            {isPublic
              ? t('trackers.trackerShareButton.publicAccess')
              : t('trackers.trackerShareButton.privateAccess')}
          </span>
          <span className="text-sm text-muted-foreground">
            {isPublic
              ? t('trackers.trackerShareButton.publicAccessHint')
              : t('trackers.trackerShareButton.privateAccessHint')}
          </span>
        </div>
        <Select
          value={value}
          onValueChange={(next) => onChange(next as Visibility)}
          disabled={disabled}
        >
          <SelectTrigger
            className="w-60 shrink-0"
            aria-label={t('trackers.trackerShareButton.generalAccessLabel')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {/* eslint-disable-next-line i18next/no-literal-string -- visibility wire value */}
            <SelectItem value="private">
              {t('trackers.trackerShareButton.onlyPeopleWithAccess')}
            </SelectItem>
            {/* eslint-disable-next-line i18next/no-literal-string -- visibility wire value */}
            <SelectItem value="public">
              {t('trackers.trackerShareButton.everyoneInTheOrganization')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </section>
  );
}
